
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Grid,
    Divider,
    alpha,
    useTheme,
    Container,
    Chip
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Badge as BadgeIcon,
    LocationOn as LocationIcon,
    Bloodtype as BloodIcon,
    Work as WorkIcon,
    Cake as CakeIcon,
    QrCode as ReferralIcon
} from '@mui/icons-material';
import { getImageUrl } from '../service/api';

export default function Profile() {
    const theme = useTheme();
    const [user, setUser] = useState({
        fullName: '',
        mobile: '',
        role: '',
        bloodGroup: '',
        constituency: '',
        occupation: '',
        dateOfBirth: '',
        autoGeneratedReferralCode: '',
        profilePicPath: null
    });

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                setUser(JSON.parse(savedUser));
            } catch (e) {
                console.error("Failed to parse user from localStorage", e);
            }
        }
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr.startsWith('0001')) return 'Not specified';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3} justifyContent="center">
                {/* Unified Profile Card */}
                <Grid item xs={12}>
                    <Paper
                        sx={{
                            borderRadius: 6,
                            overflow: 'hidden',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                            position: 'relative'
                        }}
                    >
                        <Box sx={{
                            height: 160,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        }} />
                        <Box sx={{ px: 4, pb: 4, pt: 0, position: 'relative' }}>
                            {/* Header Section - Centered */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                mt: -8,
                                mb: 4,
                                gap: 2,
                                textAlign: 'center'
                            }}>
                                <Avatar
                                    src={getImageUrl(user.profilePicPath)}
                                    sx={{
                                        width: 140,
                                        height: 140,
                                        border: '5px solid white',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                        bgcolor: 'white',
                                        color: theme.palette.primary.main
                                    }}
                                >
                                    {!user.profilePicPath && <PersonIcon sx={{ fontSize: 80 }} />}
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                                        {user.fullName || 'User Profile'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                        <Chip
                                            label={user.role || 'Administrator'}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main,
                                                fontWeight: 700,
                                                borderRadius: 1.5
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <LocationIcon sx={{ fontSize: 16 }} /> {user.constituency || 'Headquarters'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 4, opacity: 0.6 }} />

                            {/* Details Section */}
                            <Box>
                                <Typography variant="h6" sx={{
                                    fontWeight: 700,
                                    mb: 4,
                                    color: '#334155',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    justifyContent: 'center'
                                }}>
                                    <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2, display: 'flex' }}>
                                        <BadgeIcon sx={{ color: theme.palette.primary.main }} />
                                    </Box>
                                    Personal Details
                                </Typography>

                                <Grid container spacing={4}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Mobile Number</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PhoneIcon sx={{ fontSize: 18, color: 'primary.main' }} /> {user.mobile || 'Not provided'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Blood Group</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BloodIcon sx={{ fontSize: 18, color: 'error.main' }} /> {user.bloodGroup || 'Not provided'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Birthday</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CakeIcon sx={{ fontSize: 18, color: 'info.main' }} /> {formatDate(user.dateOfBirth)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Occupation</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <WorkIcon sx={{ fontSize: 18, color: 'warning.main' }} /> {user.occupation || 'Professional'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Membership Info - Commented Out */}
                {/* 
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 4, borderRadius: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.03)', height: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 4, color: '#334155', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ p: 1, bgcolor: alpha(theme.palette.secondary.main, 0.1), borderRadius: 2, display: 'flex' }}>
                                <ReferralIcon sx={{ color: theme.palette.secondary.main }} />
                            </Box>
                            Membership Info
                        </Typography>

                        <Box sx={{ mb: 4, p: 2.5, bgcolor: '#f8fafc', borderRadius: 4, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Referral Code</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.primary.main, mt: 0.5, letterSpacing: 1 }}>
                                {user.autoGeneratedReferralCode || 'N/A'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Membership ID</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>{user.membershipId || 'MEM-7829-01'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Account Status</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>Active</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
                */}
            </Grid>
        </Container>
    );
}
